import React, { useState, useEffect, useRef } from 'react';
import { Typography, IconButton, Alert, Link } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const AnnouncementToolbar = ({ isAuthenticated, message, linkText, linkHref, onAction, onHeightChange }) => {
    const [isVisible, setIsVisible] = useState(true);
    const ref = useRef(null);
    const theme = useTheme();

    // Report height changes when visibility or authentication status changes
    useEffect(() => {
        if (isAuthenticated || !isVisible) {
            if (onHeightChange) {
                onHeightChange(0);
            }
        }
    }, [isAuthenticated, isVisible, onHeightChange]);

    // Measure height after component renders
    useEffect(() => {
        if (ref.current && !isAuthenticated && isVisible) {
            const measuredHeight = ref.current.offsetHeight;
            
            if (onHeightChange) {
                onHeightChange(measuredHeight);
            }
        }
    });

    // Don't render if authenticated or not visible
    if (isAuthenticated || !isVisible) {
        return null;
    }

    const handleClose = () => {
        setIsVisible(false);
        if (onHeightChange) {
            onHeightChange(0);
        }
    };

    const handleLinkClick = () => {
        if (onAction) {
            onAction();
        }
    };

    return (
        <Alert
            ref={ref}
            severity="info"
            icon={false}
            sx={{
                borderRadius: 0,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                backgroundColor: theme.palette.tertiary.main,
                color: 'white',
                boxShadow: theme.shadows[2],
                '& .MuiAlert-message': {
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: "0",
                    justifyContent: 'center',
                    width: '100%',
                    py: 1,
                    px: 2,
                },
                '& .MuiAlert-action': {
                    padding: 0,
                    marginRight: theme.spacing(1),
                    alignItems: 'center',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                    color: 'white',
                }
            }}
            action={
                <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={handleClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            }
        >
            <Typography variant="body2" component="span" sx={{ textAlign: 'center' }}>
                {message}
                {linkText && linkHref && (
                    <>
                        {' '}
                        <Link
                            href={linkHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleLinkClick}
                            sx={{
                                marginLeft: '1em',
                                color: 'inherit',
                                textDecoration: 'underline',
                                fontWeight: 'medium',
                                '&:hover': {
                                    textDecoration: 'none',
                                }
                            }}
                        >
                            {linkText}
                        </Link>
                    </>
                )}
            </Typography>
        </Alert>
    );
};

AnnouncementToolbar.defaultProps = {
    message: "Introducing agentic recipes in QFlow, plus more Spring 2026 product updates.",
    linkText: "Read the blog post",
    linkHref: "https://blog.qflow.ai/post/qflow-spring-2026-product-updates/",
};

export default AnnouncementToolbar;
